import {
    addEdge,
    applyEdgeChanges,
    Connection,
    Edge,
    EdgeChange,
    updateEdge,
} from 'reactflow';

import { ImmerGet, ImmerSet } from '../types';

import { EditableBotVersionSlice } from '@/domain/bot';
import { pushUndoAction } from 'utils/bot/state/editable-bot-fns';
import { createSimpleEdgeReactFlow } from 'utils/reactflow/edges/create-simple-edge';

/**
 * Creates ReactFlow edge-related action handlers.
 */
export const createReactflowEdgesActions = (
    set: ImmerSet,
    get: ImmerGet,
): Pick<
    EditableBotVersionSlice,
    | 'onEdgesChange'
    | 'onConnect'
    | 'setEdges'
    | 'onEdgesRemove'
    | 'onEdgesFromHandleRemove'
    | 'onEdgeUpdate'
> => ({
    onEdgesChange: (changes: EdgeChange[]) => {
        set((state) => {
            state.editingBot.edgesReactFlow = applyEdgeChanges(
                changes,
                get().editingBot.edgesReactFlow,
            );
        });
    },

    onConnect: (connection: Connection) => {
        set((state) => {
            if (
                connection.source &&
                connection.target &&
                connection.source != connection.target &&
                connection.sourceHandle
            ) {
                pushUndoAction(state, get);

                state.editingBot.edgesReactFlow = addEdge(
                    createSimpleEdgeReactFlow(
                        connection.source,
                        connection.target,
                        connection.sourceHandle,
                    ),
                    get().editingBot.edgesReactFlow,
                );
                state.changesInBotVersion = true;
            }
        });
    },

    setEdges: (newEdges: Edge[]) => {
        set((state) => {
            pushUndoAction(state, get);
            state.editingBot.edgesReactFlow = newEdges;
            state.changesInBotVersion = true;
        });
    },

    onEdgesRemove: (ids: string[]) => {
        const newEdges = get().editingBot.edgesReactFlow.filter(
            (edge) => !ids.includes(edge.id),
        );
        set((state) => {
            pushUndoAction(state, get);

            state.editingBot.edgesReactFlow = newEdges;
            state.changesInBotVersion = true;
        });
    },

    onEdgesFromHandleRemove: (ids: string[]) => {
        const newEdges = get().editingBot.edgesReactFlow.filter((edge) =>
            edge?.sourceHandle ? !ids.includes(edge?.sourceHandle) : false,
        );
        set((state) => {
            pushUndoAction(state, get);

            state.editingBot.edgesReactFlow = newEdges;
            state.changesInBotVersion = true;
        });
    },

    onEdgeUpdate: (oldEdge: Edge, newConnection: Connection) => {
        set((state) => {
            state.editingBot.edgesReactFlow = updateEdge(
                oldEdge,
                newConnection,
                state.editingBot.edgesReactFlow,
            );
            state.changesInBotVersion = true;
        });
    },
});
